import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  setDoc,
  Timestamp,
  serverTimestamp,
  db,
  auth
} from '../firebase';
import { firebaseApiFetch } from '../api/firebaseClient';
import { sumDeadheadMilesForTotal } from '../utils/deadheadEta';
import {
  RESULT_CHUNKS_SUBCOLLECTION,
  mergeChunkDocuments,
  mergeInlineAndChunks,
  isLegacyFullResults,
  sessionNeedsChunks,
} from './resultsChunking';

const getUserCollectionName = () => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('Firebase: User not authenticated, using shared collection');
    return 'Prod_assignment_results';
  }
  return `Prod_assignment_results_${user.uid}`;
};

const convertTimestamps = (data) => {
  if (!data || typeof data !== 'object') return data;
  const out = { ...data };
  if (out.timestamp && out.timestamp instanceof Timestamp) {
    out.timestamp = out.timestamp.toDate().toISOString();
  }
  return out;
};

/** Flat assignment payload for pages (drivers, summary, metadata, …). */
export const normalizeAssignmentSession = (docData) => {
  if (!docData) return null;
  const results = docData.results || {};
  return {
    ...results,
    id: docData.id,
    isPublished: docData.isPublished ?? results.isPublished ?? false,
    portalPlanPublished: docData.portalPlanPublished ?? results.portalPlanPublished ?? false,
    routePublishFailed: docData.routePublishFailed ?? false,
    routePublishFailedAt: docData.routePublishFailedAt ?? null,
  };
};

/** Supported chunk field names stored in result_chunks or inline results. */
export const ASSIGNMENT_CHUNK_TYPES = Object.freeze({
  DRIVERS: 'drivers',
  UNASSIGNED_TRIPS: 'unassigned_trips',
  METADATA: 'metadata',
  ORIGINAL_PLAN: 'original_plan',
  MULTILOAD_ROUTES: 'multiload_routes',
  DRIVER_ELIGIBILITY: 'driver_eligibility',
  SUMMARY: 'summary',
});

/** Merge main doc shell + loaded chunk fields into the flat page payload. */
export const mergeChunksToNormalized = (mainDoc, chunkDataByField) => {
  if (!mainDoc) return null;
  const inline = mainDoc.results || {};
  const results = { ...inline };
  Object.entries(chunkDataByField || {}).forEach(([field, value]) => {
    if (value !== undefined && value !== null) {
      results[field] = value;
    }
  });
  return normalizeAssignmentSession({ ...mainDoc, results });
};

function tripsIncludeMultiload(trips) {
  return (trips || []).some(
    (t) => t?.is_multiload === true && t?.multiload_group_id != null && String(t.multiload_group_id).trim() !== ''
  );
}

/** Maps page: strip drivers to coords/header fields until a driver is selected. */
export const projectDriversForMapsList = (drivers) =>
  (drivers || []).map((d) => {
    const trips = d.trips || [];
    return {
      driver_id: d.driver_id,
      driver_name: d.driver_name,
      current_latitude: d.current_latitude,
      current_longitude: d.current_longitude,
      ref_latitude: d.ref_latitude,
      ref_longitude: d.ref_longitude,
      driver_latitude: d.driver_latitude,
      driver_longitude: d.driver_longitude,
      latitude: d.latitude,
      longitude: d.longitude,
      shift_window: d.shift_window,
      freeze_type: d.freeze_type,
      is_busy: d.is_busy,
      profileImageURL: d.profileImageURL,
      trip_count: trips.length,
      total_distance_miles: d.total_distance_miles,
      total_deadhead_miles: sumDeadheadMilesForTotal(trips),
      has_multiload: tripsIncludeMultiload(trips),
      fleet_windows: d.fleet_windows,
      planning_mode: d.planning_mode,
      has_no_fleet_window_trips: d.has_no_fleet_window_trips,
      trips: [],
    };
  });

export const attachDriverTrips = (liteDrivers, fullDrivers, driverId) => {
  const full = (fullDrivers || []).find((d) => String(d.driver_id) === String(driverId));
  if (!full) return null;
  const lite = (liteDrivers || []).find((d) => String(d.driver_id) === String(driverId));
  return lite ? { ...lite, trips: full.trips || [] } : full;
};

export const detectChunkDataMissing = (mainDoc, chunkType, chunkData) => {
  if (!mainDoc?.result_chunks?.[chunkType]) return false;
  if (chunkData === undefined || chunkData === null) return true;
  if (Array.isArray(chunkData) && chunkData.length === 0) return true;
  return false;
};

/** Main session document only (summary inline, manifest, publish flags — no chunk subcollection reads). */
export const loadSessionMainDoc = async (resultId) => {
  const user = auth.currentUser;
  if (!user) throw new Error('USER_NOT_AUTHENTICATED');

  const userCollectionName = getUserCollectionName();
  const resultDocRef = doc(db, userCollectionName, resultId);
  const docSnap = await getDoc(resultDocRef);

  if (!docSnap.exists()) return null;

  const docData = convertTimestamps(docSnap.data());
  docData.id = docSnap.id;
  if (isLegacyFullResults(docData)) {
    docData.results = docData.results || {};
  } else {
    docData.results = docData.results || {};
  }
  return docData;
};

const fetchChunkDocsForManifestField = async (resultDocRef, manifest, field) => {
  const entry = manifest?.[field];
  if (!entry?.docs?.length) return [];
  const snaps = await Promise.all(
    entry.docs.map(async (chunkDocId) => {
      const chunkRef = doc(resultDocRef, RESULT_CHUNKS_SUBCOLLECTION, chunkDocId);
      const snap = await getDoc(chunkRef);
      return snap.exists() ? snap.data() : null;
    })
  );
  return snaps.filter(Boolean);
};

/** Resolve one chunk field using an already-loaded main doc (no extra main-doc read). */
export const resolveChunkFromMainDoc = async (resultDocRef, mainDoc, chunkType) => {
  const inline = mainDoc.results || {};

  if (isLegacyFullResults(mainDoc)) {
    return inline[chunkType] ?? null;
  }

  if (inline[chunkType] !== undefined) {
    return inline[chunkType];
  }

  if (!sessionNeedsChunks(mainDoc)) {
    return inline[chunkType] ?? null;
  }

  const manifest = mainDoc.result_chunks || {};
  const chunkDocs = await fetchChunkDocsForManifestField(resultDocRef, manifest, chunkType);
  if (!chunkDocs.length) return null;

  const merged = mergeChunkDocuments(chunkDocs);
  return merged[chunkType] ?? null;
};

/**
 * Load main doc once, then fetch all requested chunk types in parallel.
 * Uses single backend API call (replaces N mock-Firestore getDoc requests).
 */
export const loadSessionChunksBatch = async (resultId, chunkTypes, options = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('USER_NOT_AUTHENTICATED');

  const fields = [...new Set((chunkTypes || []).filter(Boolean))];
  const params = new URLSearchParams({ result_id: String(resultId) });
  if (fields.length) params.set('fields', fields.join(','));
  if (options.driversLite) params.set('drivers_lite', '1');

  const res = await firebaseApiFetch(`/get-session-chunks?${params.toString()}`);
  if (res.success === false) {
    throw new Error(res.error || 'Failed to load session chunks');
  }

  const mainDoc = res.main_doc ? convertTimestamps(res.main_doc) : null;
  if (mainDoc) {
    mainDoc.id = mainDoc.id || resultId;
    mainDoc.results = mainDoc.results || {};
  }

  return { mainDoc, chunks: res.chunks || {} };
};

/**
 * Load one chunk field for a session (manifest-targeted chunk docs only).
 * Returns raw merged field value (array/object), not the full normalized session.
 */
export const loadAssignmentChunk = async (resultId, chunkType) => {
  const { mainDoc, chunks } = await loadSessionChunksBatch(resultId, [chunkType]);
  if (!mainDoc) return null;
  return chunks[chunkType] ?? null;
};

const fetchResultChunks = async (resultDocRef) => {
  const chunksRef = collection(resultDocRef, RESULT_CHUNKS_SUBCOLLECTION);
  const snap = await getDocs(chunksRef);
  return snap.docs.map((d) => d.data());
};

/** Parallel: result_chunks + optional additional_logs. */
const fetchSubcollectionsParallel = async (resultDocRef, { needsChunks, needsAdditionalLogs }) => {
  const tasks = [];
  if (needsChunks) {
    tasks.push(fetchResultChunks(resultDocRef));
  } else {
    tasks.push(Promise.resolve([]));
  }
  if (needsAdditionalLogs) {
    const logsRef = collection(resultDocRef, 'additional_logs');
    tasks.push(getDocs(query(logsRef, orderBy('batch_number', 'asc'))));
  } else {
    tasks.push(Promise.resolve(null));
  }
  const [chunkDocs, logsSnap] = await Promise.all(tasks);
  return { chunkDocs, logsSnap };
};

const mergeLogsFromMainAndSnap = (resultData, logsSnap) => {
  const allLogs = [];
  if (resultData.logs && Array.isArray(resultData.logs)) {
    allLogs.push(...resultData.logs);
  }
  if (logsSnap) {
    logsSnap.forEach((batchDoc) => {
      const batchData = convertTimestamps(batchDoc.data());
      if (batchData.logs && Array.isArray(batchData.logs)) {
        allLogs.push(...batchData.logs);
      }
    });
  }
  return allLogs;
};

/**
 * Load main doc + merged result_chunks (v1/v2).
 */
export const loadFullAssignmentResult = async (resultId) => {
  const user = auth.currentUser;
  if (!user) throw new Error('USER_NOT_AUTHENTICATED');

  const userCollectionName = getUserCollectionName();
  const resultDocRef = doc(db, userCollectionName, resultId);
  const docSnap = await getDoc(resultDocRef);

  if (!docSnap.exists()) return null;

  const docData = convertTimestamps(docSnap.data());
  docData.id = docSnap.id;

  if (isLegacyFullResults(docData)) {
    docData.results = docData.results || {};
    return docData;
  }

  const needsChunks = sessionNeedsChunks(docData);
  const { chunkDocs } = await fetchSubcollectionsParallel(resultDocRef, {
    needsChunks,
    needsAdditionalLogs: false,
  });

  if (needsChunks) {
    docData.results = mergeInlineAndChunks(docData.results || {}, mergeChunkDocuments(chunkDocs));
  } else {
    docData.results = docData.results || {};
  }
  return docData;
};

/**
 * Full session + logs in one main read + parallel subcollections.
 */
export const loadSessionWithLogs = async (resultId) => {
  const user = auth.currentUser;
  if (!user) throw new Error('USER_NOT_AUTHENTICATED');

  const userCollectionName = getUserCollectionName();
  const resultDocRef = doc(db, userCollectionName, resultId);
  const docSnap = await getDoc(resultDocRef);

  if (!docSnap.exists()) {
    return { assignment: null, logs: { result_id: resultId, log_count: 0, logs: [], summary: {} } };
  }

  const docData = convertTimestamps(docSnap.data());
  docData.id = docSnap.id;

  const needsChunks = sessionNeedsChunks(docData);
  const needsAdditionalLogs = Boolean(docData.has_additional_logs);

  const { chunkDocs, logsSnap } = await fetchSubcollectionsParallel(resultDocRef, {
    needsChunks,
    needsAdditionalLogs,
  });

  let chunkDataMissing = false;
  if (needsChunks) {
    const chunkMerged = mergeChunkDocuments(chunkDocs);
    docData.results = mergeInlineAndChunks(docData.results || {}, chunkMerged);
    const manifestFields = Object.keys(docData.result_chunks || {});
    if (manifestFields.length > 0) {
      const missingFields = manifestFields.filter((field) => docData.results[field] === undefined);
      chunkDataMissing = chunkDocs.length === 0 || missingFields.length > 0;
    }
  } else {
    docData.results = docData.results || {};
  }

  const allLogs = mergeLogsFromMainAndSnap(docData, logsSnap);
  const summary = docData.results?.summary || {};

  return {
    assignment: docData,
    chunkDataMissing,
    logs: {
      result_id: resultId,
      log_count: allLogs.length,
      logs: allLogs,
      summary,
      has_additional_logs: needsAdditionalLogs,
      main_logs_count: docData.logs?.length || 0,
      subcollection_logs_count: allLogs.length - (docData.logs?.length || 0),
    },
  };
};

export const getLatestAssignmentResults = async () => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('USER_NOT_AUTHENTICATED');

    const userCollectionName = getUserCollectionName();
    const q = query(collection(db, userCollectionName), orderBy('timestamp', 'desc'), limit(1));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) return null;
    const first = querySnapshot.docs[0];
    return loadFullAssignmentResult(first.id);
  } catch (error) {
    console.error('Error fetching latest assignment results:', error);
    if (error.message === 'USER_NOT_AUTHENTICATED') throw error;
    throw new Error('Failed to fetch latest assignment results');
  }
};

/** Session list — main doc only (summary), no chunk load. */
export const getAssignmentHistory = async (limitCount = 10) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('USER_NOT_AUTHENTICATED');

    const userCollectionName = getUserCollectionName();

    const q = query(
      collection(db, userCollectionName),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const results = [];

    querySnapshot.forEach((docSnap) => {
      const data = convertTimestamps(docSnap.data());
      const inline = data.results || {};
      results.push({
        id: docSnap.id,
        timestamp: data.timestamp,
        planning_date: data.planning_date,
        isPublished: data.isPublished ?? inline.isPublished ?? false,
        portalPlanPublished: data.portalPlanPublished ?? inline.portalPlanPublished ?? false,
        routePublishFailed: data.routePublishFailed ?? false,
        results_storage_version: data.results_storage_version,
        has_result_chunks: data.has_result_chunks ?? false,
        results: {
          summary: inline.summary || {},
        },
      });
    });

    if (results.length === 0) throw new Error('NO_USER_DATA_RUN_PLANNER_FIRST');
    return results;
  } catch (error) {
    console.error('Error fetching assignment history:', error);
    if (error.message === 'USER_NOT_AUTHENTICATED' || error.message === 'NO_USER_DATA_RUN_PLANNER_FIRST') {
      throw error;
    }
    throw new Error('Failed to fetch assignment history');
  }
};

export const getAssignmentResultById = async (resultId) => {
  try {
    if (!auth.currentUser) throw new Error('USER_NOT_AUTHENTICATED');
    return loadFullAssignmentResult(resultId);
  } catch (error) {
    console.error('Error fetching assignment result by ID:', error);
    if (error.message === 'USER_NOT_AUTHENTICATED') throw error;
    throw new Error('Failed to fetch assignment result');
  }
};

export const getAssignmentLogs = async (resultId, options = {}) => {
  try {
    const params = new URLSearchParams({
      result_id: String(resultId),
      all: '1',
    });
    const res = await firebaseApiFetch(`/get-session-logs?${params.toString()}`);
    const logs = res.logs || [];
    const logCount = res.log_count ?? logs.length;
    if (logCount > 0 && logs.length < logCount) {
      throw new Error(`Incomplete logs response (${logs.length} of ${logCount})`);
    }
    return {
      result_id: res.result_id || resultId,
      log_count: logCount,
      logs,
      summary: res.summary || {},
      offset: 0,
      limit: logCount,
      has_more: false,
      has_additional_logs: Boolean(res.has_additional_logs),
    };
  } catch (error) {
    console.error('Error fetching assignment logs:', error);
    throw error;
  }
};

export const getDriverEligibilityPage = async (resultId, options = {}) => {
  const params = new URLSearchParams({
    result_id: String(resultId),
    all: '1',
  });
  const res = await firebaseApiFetch(`/get-session-eligibility?${params.toString()}`);
  const driverDetails = res.driver_details || [];
  const totalDrivers = res.total_drivers ?? driverDetails.length;
  if (totalDrivers > 0 && driverDetails.length < totalDrivers) {
    throw new Error(`Incomplete eligibility response (${driverDetails.length} of ${totalDrivers})`);
  }
  return {
    result_id: res.result_id || resultId,
    eligible_drivers: res.eligible_drivers ?? 0,
    ineligible_drivers: res.ineligible_drivers ?? 0,
    total_drivers: totalDrivers,
    driver_details: driverDetails,
    offset: 0,
    limit: totalDrivers,
    has_more: false,
  };
};

const CHUNK_RESULT_KEYS = new Set(['drivers', 'unassigned_trips', 'results.drivers', 'results.unassigned_trips']);

export const updateAssignmentResult = async (resultId, updateData) => {
  if (!auth.currentUser) throw new Error('USER_NOT_AUTHENTICATED');

  const drivers = updateData?.['results.drivers'] ?? updateData?.drivers;
  const unassigned = updateData?.['results.unassigned_trips'] ?? updateData?.unassigned_trips;
  if (drivers !== undefined || unassigned !== undefined) {
    return updateAssignmentChunks(resultId, { drivers, unassigned_trips: unassigned });
  }

  const chunkKey = Object.keys(updateData || {}).find((k) => CHUNK_RESULT_KEYS.has(k));
  if (chunkKey) {
    throw new Error(`Use updateAssignmentChunks for ${chunkKey}, not updateAssignmentResult`);
  }

  return updateAssignmentMetadata(resultId, updateData);
};

/** Publish / route flags — main doc only (TimeFilter, Bucket publish). */
export const updateAssignmentMetadata = async (resultId, patch) => {
  if (!auth.currentUser) throw new Error('USER_NOT_AUTHENTICATED');
  await firebaseApiFetch('/update-result-metadata', {
    method: 'PATCH',
    body: { result_id: resultId, patch },
  });
};

/**
 * Soft-delete a planning session (isDeleted on main doc).
 * Hidden from session list; remains in Mongo until the 15-day hard purge.
 */
export const softDeleteAssignmentSession = async (resultId) => {
  if (!auth.currentUser) throw new Error('USER_NOT_AUTHENTICATED');
  if (!resultId) throw new Error('Missing resultId');
  await firebaseApiFetch('/soft-delete-session', {
    method: 'POST',
    body: { result_id: resultId },
  });
  return true;
};

/** Bucket manual edits — session-scoped chunk writes on the server. */
export const updateAssignmentChunks = async (resultId, { drivers, unassigned_trips }) => {
  if (!auth.currentUser) throw new Error('USER_NOT_AUTHENTICATED');

  const body = { result_id: resultId };
  if (drivers !== undefined) body.drivers = drivers;
  if (unassigned_trips !== undefined) body.unassigned_trips = unassigned_trips;

  await firebaseApiFetch('/update-result-chunks', { method: 'PATCH', body });
  return true;
};

export const appendAssignmentLog = async (resultId, logEntry) => {
  if (!auth.currentUser) throw new Error('USER_NOT_AUTHENTICATED');
  await firebaseApiFetch('/append-session-log', {
    method: 'POST',
    body: { result_id: resultId, log_entry: logEntry },
  });
};

export const updateAssignmentChunksAndAppendLog = async (resultId, { drivers, unassigned_trips }, logEntry) => {
  await updateAssignmentChunks(resultId, { drivers, unassigned_trips });
  await appendAssignmentLog(resultId, logEntry);
};

/** @deprecated Use updateAssignmentChunks + appendAssignmentLog */
export const updateAssignmentResultAndAppendLog = async (resultId, updatePayload, logEntry) => {
  const drivers = updatePayload['results.drivers'] ?? updatePayload.drivers;
  const unassigned = updatePayload['results.unassigned_trips'] ?? updatePayload.unassigned_trips;
  if (drivers !== undefined || unassigned !== undefined) {
    return updateAssignmentChunksAndAppendLog(
      resultId,
      { drivers, unassigned_trips: unassigned },
      logEntry
    );
  }
  await updateAssignmentMetadata(resultId, updatePayload);
  await appendAssignmentLog(resultId, logEntry);
};

export const buildDriverSelectionTemplateDocId = (companyId, serverEnvironment) => {
  const raw = String(companyId ?? '').trim();
  if (!raw) return null;
  const safe = raw
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 200);
  if (!safe) return null;
  const env = serverEnvironment === 'live' ? 'live' : 'dev';
  return `${safe}__${env}`;
};

const driverSelectionTemplateRef = (uid, companyId, serverEnvironment) => {
  const templateId = buildDriverSelectionTemplateDocId(companyId, serverEnvironment);
  if (!templateId) throw new Error('DRIVER_TEMPLATE_SCOPE_INVALID');
  return doc(db, 'Templates', uid, 'driver_selection_templates', templateId);
};

export const getDriverSelectionTemplate = async (companyId, serverEnvironment) => {
  const user = auth.currentUser;
  if (!user) throw new Error('USER_NOT_AUTHENTICATED');
  const templateId = buildDriverSelectionTemplateDocId(companyId, serverEnvironment);
  if (!templateId) return { exists: false, drivers: [], skipped: true };
  const snap = await getDoc(driverSelectionTemplateRef(user.uid, companyId, serverEnvironment));
  if (!snap.exists()) return { exists: false, drivers: [] };
  const data = snap.data();
  return { exists: true, drivers: Array.isArray(data.drivers) ? data.drivers : [] };
};

export const saveDriverSelectionTemplate = async (payload, companyId, serverEnvironment) => {
  const user = auth.currentUser;
  if (!user) throw new Error('USER_NOT_AUTHENTICATED');
  const templateId = buildDriverSelectionTemplateDocId(companyId, serverEnvironment);
  if (!templateId) throw new Error('DRIVER_TEMPLATE_SCOPE_INVALID');
  const env = serverEnvironment === 'live' ? 'live' : 'dev';
  await setDoc(driverSelectionTemplateRef(user.uid, companyId, serverEnvironment), {
    drivers: payload.drivers || [],
    companyId: String(companyId).trim(),
    serverEnvironment: env,
    updatedAt: serverTimestamp(),
  });
};
